import MantineTable from '../../../../common/table/mantine';
import { useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Button, Checkbox, Grid, NumberInput, Skeleton, Switch, Tabs } from '@mantine/core';
import { CustomTextArea } from '../../../../common/inputs/CustomTextArea';
import { IconRefresh } from '@tabler/icons-react';
import { axios } from '../../../../utils/axios';
import { useParams } from 'react-router-dom';
import { AppLoader } from '../../../../components/common/loader';
import { CostingRequestsContext, useCrStatusCancelled } from '../../../../contexts/CostingRequest';
import { AuthContext } from '../../../../contexts/AuthContext';
import { showAlert } from '../../../../utils/alerts';
import { useSwitchStyles } from '../budgetBomAdjustment';
import { NpsComponent } from '../../../../common/nps';
import { crRoles, NpsIds } from '../../../../constants';
import AssignedUsersList from '../../../../components/assignedUsersList';

export const Packmat = ({ loading, skus, fetch, activePlant, setActivePlant, type }) => {
  const { classes } = useSwitchStyles();
  const isCancelled = useCrStatusCancelled();
  const { crNumber } = useParams();
  const { info, isCrRoleAuthorized, crStepStatus } = useContext(CostingRequestsContext);
  const {
    user: { user: userId },
  } = useContext(AuthContext);
  const [saveLoader, setSaveLoader] = useState({});
  const [form, setForm] = useState({});
  const [onlyEditable, setOnlyEditable] = useState(false);
  const [npsOpened, setNpsOpened] = useState(false);
  const { producing_plant_data = [], types_of_sku } = info;

  const handleSubmit = async (data) => {
    try {
      const {
        id,
        cost_value,
        material_number,
        production_plant,
        not_procurement_material,
        promotional_material,
        planned_price_date_1,
      } = data;

      if (form[id]?.promotional_material && !form[id]?.comment?.trim()) {
        showAlert(
          { message: 'Comment is required when "No Material Price Required" is selected.' },
          'error',
        );
        return;
      }
      const payload = {
        ...form[id],
        not_procurement_material: form[id].hasOwnProperty('not_procurement_material')
          ? form[id].not_procurement_material
          : not_procurement_material,
        promotional_material: form[id]?.promotional_material ?? promotional_material,
        planned_price_1: form[id].planned_price_1 ? form[id].planned_price_1 : cost_value,
        planned_price_date_1: planned_price_date_1 || moment().format('YYYY-MM-DD'),
        material: material_number,
        plant: production_plant,
      };

      setSaveLoader({ ...saveLoader, [id]: true });
      setForm({ ...form, [id]: null });
      await axios.post('costing-request/step/bom-component-price', payload);
      await fetch();
      setNpsOpened(true);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Server error. Please try again.';
      showAlert({ message: errorMessage }, 'error');
    } finally {
      setSaveLoader({ ...saveLoader, [data.id]: false });
    }
  };

  const handleChange = (value, name, id, component_id) => {
    const values = { ...form };

    if (!values[id])
      values[id] = {
        costing_request_id: crNumber,
        id: id,
        component_id: component_id,
      };
    values[id][name] = value;

    setForm(values);
  };

  const getNumberInputValue = (costValue, formComment) => {
    return parseFloat(costValue || 0) || parseFloat(formComment || 0) || 0;
  };

  const isDisabled = (data) => {
    const {
      cost_value,
      fpna_analyst_responsible_id,
      not_procurement_material,
      promotional_material,
      id,
      manual_closed,
    } = data;

    const userPermissions = {
      procurement: isCrRoleAuthorized(activePlant, crRoles.procurement),
      standardTransferPricing: isCrRoleAuthorized(activePlant),
    };

    const notProcurement =
      typeof form[id]?.not_procurement_material !== 'undefined'
        ? form[id]?.not_procurement_material
        : not_procurement_material;

    const costValue =
      typeof form[id]?.cost_value !== 'undefined' ? form[id]?.cost_value : cost_value;

    const isVicClosed =
      (crStepStatus?.[activePlant]?.['VIC Submission'] || '').toLowerCase() === 'closed';

    const costValueDisabled = (costValue > 0 && !manual_closed) || isVicClosed;

    const permission =
      type === 'repack' ? userPermissions.standardTransferPricing : userPermissions.procurement;

    const everythingDisabled = !permission || costValueDisabled || isVicClosed || isCancelled;

    const notProcurementMaterialDisabled = not_procurement_material
      ? !userPermissions.standardTransferPricing
      : !permission;

    const promotionalMaterialDisabled = promotional_material
      ? !userPermissions.standardTransferPricing
      : !permission;

    return {
      cost_value:
        (notProcurement && !userPermissions.standardTransferPricing) ||
        notProcurementMaterialDisabled ||
        everythingDisabled,
      not_procurement_material: notProcurementMaterialDisabled || costValueDisabled || isCancelled,
      promotional_material: promotionalMaterialDisabled || costValueDisabled || isCancelled,
      comment: everythingDisabled,
      submit: everythingDisabled,
    };
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'mat_group_desc',
        header: 'Mat Group Desc',
      },
      {
        accessorKey: 'material_number',
        header: 'Mat No',
      },
      {
        accessorKey: 'material_description',
        header: 'Mat Desc',
      },
      {
        accessorKey: 'material_status',
        header: 'Status',
      },
      {
        accessorKey: 'not_procurement_material',
        header: 'Not a Procurement Material',
        Cell: ({ row }) => {
          const { not_procurement_material, component_id, id } = row.original;
          return (
            <Skeleton visible={saveLoader[id] || false}>
              <Checkbox
                label=''
                checked={form[id] ? form[id].not_procurement_material : not_procurement_material}
                onChange={(event) =>
                  handleChange(event.target.checked, 'not_procurement_material', id, component_id)
                }
                disabled={isDisabled(row.original).not_procurement_material}
              />
            </Skeleton>
          );
        },
      },
      {
        accessorKey: 'promotional_material',
        header: 'No material price required',
        Cell: ({ row }) => {
          const { promotional_material, component_id, id } = row.original;
          return (
            <Skeleton visible={saveLoader[id] || false}>
              <Checkbox
                label=''
                checked={form[id] ? form[id].promotional_material : promotional_material}
                onChange={(event) =>
                  handleChange(event.target.checked, 'promotional_material', id, component_id)
                }
                disabled={isDisabled(row.original).promotional_material}
              />
            </Skeleton>
          );
        },
      },

      {
        accessorKey: 'procurement_spoc',
        header: 'Procurement SPOC',
        Cell: ({
          row: {
            original: { procurement_spoc },
          },
        }) => <AssignedUsersList users={procurement_spoc} />,
      },

      {
        accessorKey: 'producing_plant',
        header: 'Prod. Plant',
      },
      {
        accessorKey: 'base_unit',
        header: 'Base Unit',
      },
      {
        accessorKey: 'uom',
        header: 'UOM',
      },
      {
        accessorKey: 'currency',
        header: 'Currency',
      },
      {
        accessorKey: 'cost_value',
        header: 'Cost Value',
        Cell: ({ row }) => {
          const { cost_value, component_id, id } = row.original;
          return (
            <Skeleton visible={saveLoader[id] || false}>
              <NumberInput
                value={getNumberInputValue(form[id]?.planned_price_1, cost_value)}
                precision={3}
                min={0.01}
                step={0.01}
                max={1000000}
                disabled={isDisabled(row.original).cost_value}
                onChange={(value) => handleChange(value, 'planned_price_1', id, component_id)}
              />
            </Skeleton>
          );
        },
      },
      {
        accessorKey: 'pack_mat_price_requesting_date',
        header: 'Requested On',
      },
      {
        accessorKey: 'price_updated',
        header: 'Submitted On',
      },

      {
        accessorKey: 'fpna_analyst_responsible',
        header: 'FP&A GCC SME',
        Cell: ({
          row: {
            original: { fpna_analyst_responsible },
          },
        }) => <AssignedUsersList users={fpna_analyst_responsible} />,
      },
      {
        accessorKey: 'comments',
        header: 'Comments',
        Cell: ({ row }) => {
          const { comment, component_id, id, cost_value } = row.original;
          const isCommentMandatory = form[id]?.promotional_material;
          const showError = isCommentMandatory && !form[id]?.comment?.trim();

          return (
            <Skeleton visible={saveLoader[id] || false}>
              <CustomTextArea
                radius='md'
                value={form[id] ? form[id]?.comment : comment || ''}
                name='comment'
                onChange={(value) => handleChange(value, 'comment', id, component_id)}
                disabled={isDisabled(row.original).comment}
                error={
                  showError
                    ? 'Comment is required when "No Material Price Required" is selected.'
                    : ''
                }
              />
            </Skeleton>
          );
        },
      },
      {
        id: 'action',
        header: 'Action',
        Cell: ({ row }) => {
          const { id } = row.original;
          return (
            <Button
              disabled={!form[id] || saveLoader[id] || isDisabled(row.original).submit}
              color='dark'
              fullWidth
              size='sm'
              radius='sm'
              className='add-supplier-button'
              onClick={() => handleSubmit(row.original)}
            >
              {saveLoader[id] ? <AppLoader size='sm' /> : 'Submit'}
            </Button>
          );
        },
      },
    ],
    [skus, form, saveLoader, onlyEditable],
  );

  const rightButtons = [
    {
      button: (
        <Button
          variant='light'
          color='gray.6'
          onClick={() => {
            setForm({});
            fetch(true);
          }}
        >
          <IconRefresh color='black' variant='filled' size={16} />
        </Button>
      ),
    },
  ];

  const leftButtons = [
    {
      button: (
        <Switch
          label='View only components that require price inputs'
          checked={onlyEditable}
          onChange={(event) => setOnlyEditable(event.currentTarget.checked)}
          classNames={{ input: classes.input, thumb: classes.thumb, track: classes.track }}
        />
      ),
    },
  ];

  return (
    <>
      <Tabs
        keepMounted={false}
        value={activePlant}
        onTabChange={(value) => setActivePlant(value)}
        color='yellow'
      >
        <Tabs.List>
          {producing_plant_data.map(({ producing_plant }, index) => (
            <Tabs.Tab value={producing_plant} key={index} className='menu-tabs'>
              {producing_plant}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {producing_plant_data.map(({ producing_plant }, index) => {
          return (
            <Tabs.Panel value={producing_plant} key={index}>
              <Grid>
                <Grid.Col span={12}>
                  <MantineTable
                    columns={columns}
                    initialData={(skus || []).filter((sku) =>
                      !sku.asm && sku.producing_plant === producing_plant && !onlyEditable
                        ? true
                        : sku.material_type !== 'Z005' &&
                          !(sku.cost_value > 0) &&
                          (sku.status === 'In Progress' || sku.manual_closed) &&
                          (types_of_sku === 'Repack'
                            ? type === 'repack'
                              ? sku.material_number.replace(/^0+/, '').startsWith('2')
                              : !sku.material_number.replace(/^0+/, '').startsWith('2')
                            : true),
                    )}
                    unique={'sku'}
                    enableRowSelection={false}
                    loading={loading}
                    showSelectedToggle={false}
                    hideSelectColumn={true}
                    showResetAll={false}
                    initialState={{
                      sorting: [
                        {
                          id: 'material_description',
                          desc: false,
                        },
                      ],
                    }}
                    rightButtons={rightButtons}
                    leftButtons={leftButtons}
                    enableExport={true}
                    csvFilename={`bom_component_price_packmat_${moment().format('YYYY-MM-DD')}`}
                    pageSize={10}
                  />
                </Grid.Col>
              </Grid>
            </Tabs.Panel>
          );
        })}
        {npsOpened && (
          <NpsComponent
            crNumber={crNumber}
            opened={npsOpened}
            setOpened={setNpsOpened}
            callback={() => {
              setNpsOpened(false);
            }}
            id={NpsIds().bom}
          />
        )}
      </Tabs>
    </>
  );
};
